//TODO пересмотреть логику quoteRequestData и его использование, тк теперь quotes.quoteSelectedData есть в сторе
import { observer } from "mobx-react-lite";

import { Col, Flex, Radio, Select, Skeleton, Switch, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";

import { useStores } from "stores";
import { PlateList, PlateItem } from "components/PlateList";
import { getColumns } from "./getColumns";
import {
  AML_REQUEST_ACTION,
  BID_REQUEST_STATUS_ENUM,
  CONTRACT_DIRECTION_ENUM,
  NOTIFICATION_TYPE_ENUM,
  OPERATION_TYPE,
  QUOTE_SELECTION_ENUM,
  QUOTE_STATUS_ENUM,
  QUOTE_STATUSES,
  QuoteAcceptData,
  QuoteEntity,
  RISK_STATUS_ENUM,
} from "interfaces";
import { CountBadge, CustomButton, DefaultCheckbox, DefaultSelect, Status, WhiteBoxBlock } from "ui";
import { approveQuote, NormalizedError } from "services";
import {
  checkDaysPassed,
  dateFormat,
  isStatusIncluded,
  notificationPeriodDays,
  parseErrorMessage,
  renderSelectOptions,
} from "utils";
import { useNotificationContext } from "providers";
import { DIRECT_PAYMENT_CUSTOMER_LP, QUOTE_SELECTION } from "utils/envConstants";
import { BidOperationButton } from "../BidOperations";
import { QuoteList } from "stores/QuotesList";
import { ApproveWalletCheckButton } from "components";

import style from "./quotes.module.css";

import { ReactComponent as QuoteEmptyIcon } from "images/quoteEmptyIcon.svg";

import { QuoteFooter } from "./QuoteFooter/QuoteFooter";

import { QuoteHeader } from "./QuoteHeader";

export const Quotes = observer(() => {
  const { quotes, user, bidCard } = useStores();
  const { openNotification } = useNotificationContext();
  const { status } = bidCard.bid;

  const { isSeniorOperator, isOperator, isCustomer, isRiskManager, isProvider, isLPRiskManager } = user.roles;

  const isExp = bidCard.bid.contract.direction == CONTRACT_DIRECTION_ENUM.EXP;

  const [loading, setLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState("");
  const [showExpiredQuote, setShowExpiredQuote] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [quoteRequestData, setQuoteRequestData] = useState<QuoteAcceptData>();

  const isQuoteSelectionCustomer = QUOTE_SELECTION === QUOTE_SELECTION_ENUM.CUSTOMER;

  const isStatusNew = isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.NEW]);
  const hasTokenBalance = Number(bidCard?.bid?.balance?.token) > 0;
  const isCanApproveQuote = isStatusNew && isSeniorOperator && QUOTE_SELECTION_ENUM.OPERATOR === QUOTE_SELECTION;

  const amlCheckQuote = bidCard.amlCheckStatusData?.some(
    (i) => i.requestAction === AML_REQUEST_ACTION.ACCEPT_QUOTE && i.checksCompleted && i.checksSuccess
  );

  const hasQuoteExpired = quotes.allData.some((quote) =>
    isStatusIncluded(quote.status, [QUOTE_STATUS_ENUM.EXPIRED, QUOTE_STATUS_ENUM.CANCELED])
  );

  const enoughToken = (Number(bidCard?.bid?.balance?.token) ?? 0) >= (bidCard.bid.amount ?? 0);

  const quoteApproveDisabled = !DIRECT_PAYMENT_CUSTOMER_LP && enoughToken && isCustomer && amlCheckQuote;

  const isSeniorOperatorDaysPassed =
    isSeniorOperator &&
    isQuoteSelectionCustomer &&
    checkDaysPassed(bidCard.bid.created, notificationPeriodDays).isOverDays &&
    isStatusNew;

  const isCustomerBidStatusNew = isCustomer && isQuoteSelectionCustomer && isStatusNew;

  const disabledIsCustomerStatusNew = !quoteApproveDisabled && bidCard.bid.contract.direction !== "IMP";
  const count = quotes.paginationData.totalElements ?? 0;

  const columns = useMemo(() => getColumns(user, bidCard.bid, quotes), [user, bidCard.bid, count]);

  const showCheckbox =
    isCanApproveQuote ||
    isCustomerBidStatusNew ||
    (isSeniorOperatorDaysPassed && bidCard.bid.contract.direction === "EXP" && hasTokenBalance);

  const handleQuoteSelect = (quote: QuoteEntity) => {
    quotes.setQuoteSelectedId(quote.id);
    setSelectedQuote((prev) => (prev === quote.id ? "" : quote.id));

    if (
      selectedQuote !== quote.id &&
      (isCustomerBidStatusNew ||
        isCanApproveQuote ||
        (isSeniorOperatorDaysPassed &&
          isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.QUOTE_PROPOSED, BID_REQUEST_STATUS_ENUM.NEW]) &&
          bidCard.bid.contract.direction === "EXP"))
    ) {
      setQuoteRequestData({
        quoteId: quote.id,
        lpCompanyName: quote.lpName,
        lpCompRegNum: quote.lpRegistrationNumber,
        rate: quote.rate,
        quoteRequest: true,
      });
    }
  };

  const handleApproveQuote = async () => {
    setLoading(true);
    await approveQuote({ id: selectedQuote })
      .then(() => {
        openNotification({
          message: "Условия по заявке предложены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          quotes.fetch();
          setLoading(false);
        }, 1500);
      })
      .catch((e: NormalizedError) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setSelectedQuote("");
        setLoading(false);
        setQuoteRequestData(undefined);
      });
  };

  useEffect(() => {
    if (showExpiredQuote) {
      quotes.onChangeDataFlow({
        filters: {
          onlyActive: showExpiredQuote,
        },
      });
    } else {
      quotes.onChangeDataFlow({ filters: { onlyActive: undefined } });
    }
  }, [showExpiredQuote]);

  useEffect(() => {
    if (hasQuoteExpired) {
      setShowFilter(true);
    }
  }, [hasQuoteExpired]);

  return (
    <div id="bid-quotes">
      <WhiteBoxBlock
        collapse={{
          title: (
            <Flex justify="space-between" align="center">
              <Flex gap={4} align="center">
                <Typography.Text className="whitebox-title">Котировки</Typography.Text>
                {count > 0 && <CountBadge count={count} />}
              </Flex>
            </Flex>
          ),
          defaultOpen: true,
        }}
      >
        <Flex vertical gap="large">
          <Flex gap="small" justify="end">
            {showFilter && (
              <Flex align="center" gap="small">
                <DefaultCheckbox onChange={() => setShowExpiredQuote((prev) => !prev)} checked={showExpiredQuote} />
                <Typography.Text>Скрыть неактивные котировки</Typography.Text>
              </Flex>
            )}
          </Flex>

          {quotes.fetchStatus === "fetching" ? (
            <Skeleton active />
          ) : (
            <PlateList<QuoteEntity, QuoteList>
              store={quotes}
              columns={columns}
              PlateComponent={PlateItem}
              updateWithInterval
              emptyProps={{
                children: (
                  <Flex justify="center">
                    <Typography.Text className="text-description" style={{ marginTop: "-10px" }}>
                      Ожидайте предложений — они сразу отобразятся здесь
                    </Typography.Text>
                  </Flex>
                ),
                icon: <QuoteEmptyIcon />,
                description: (
                  <Typography.Text className="text-lg" strong>
                    Котировок ещё нет
                  </Typography.Text>
                ),
              }}
              errorMessage="Ошибка загрузки котировок"
              showPagination
              headerItemComponent={(item) => <QuoteHeader quote={item} />}
              footerItemComponent={(item) => <QuoteFooter item={item} />}
              onRow={(item) => ({
                className: `${selectedQuote === item.id ? style.rowItemActive : ""}`,
                onClick:
                  showCheckbox &&
                  !isStatusIncluded(item.status, [QUOTE_STATUS_ENUM.EXPIRED, QUOTE_STATUS_ENUM.CANCELED]) &&
                  item.riskStatus !== RISK_STATUS_ENUM.APPROVE_NEEDED
                    ? () => handleQuoteSelect(item)
                    : undefined,
              })}
              checkbox={
                showCheckbox
                  ? {
                      getCheckboxProps: (item) => ({
                        id: item.id,
                        checked: item.id === selectedQuote,
                        disabled:
                          loading ||
                          isStatusIncluded(item.status, [QUOTE_STATUS_ENUM.EXPIRED, QUOTE_STATUS_ENUM.CANCELED]) ||
                          item.riskStatus === RISK_STATUS_ENUM.APPROVE_NEEDED,
                      }),
                    }
                  : undefined
              }
            />
          )}

          <Flex justify="space-between">
            <Col>
              {isCanApproveQuote && !isSeniorOperatorDaysPassed && (
                <CustomButton
                  htmlType="submit"
                  disabled={!selectedQuote || loading}
                  loading={loading}
                  onClick={handleApproveQuote}
                >
                  Утвердить условия
                </CustomButton>
              )}

              {isCustomerBidStatusNew && (
                <BidOperationButton
                  id="quote-id-accept-button-customer"
                  disabled={!selectedQuote || (!quoteApproveDisabled && bidCard.bid.contract.direction !== "IMP")}
                  operationType={OPERATION_TYPE.REQUEST_APPROVE}
                  quoteRequestData={quoteRequestData}
                  btnTitle="Принять условия"
                  btnTooltip={
                    !quoteApproveDisabled && bidCard.bid.contract.direction !== "IMP"
                      ? "Нельзя принять котировку до поступления криптовалюты и окончания всех проверок"
                      : undefined
                  }
                  modalTitle="Принять условия"
                  messageTitle={`Принимаю условия на${" "}
                ${bidCard.bid.contract.direction === "EXP" ? "продажу " : "покупку "}цифровой валюты со следующими параметрами:`}
                  tooltip={`Данное действие является ${!DIRECT_PAYMENT_CUSTOMER_LP ? "Юридически значимым" : "подписанием Договора купли-продажи ЦВ и является юридически значимым"}`}
                />
              )}

              {isSeniorOperatorDaysPassed && bidCard.bid.contract.direction === "EXP" && hasTokenBalance && (
                <BidOperationButton
                  id="quote-id-accept-button-operator-days-passed"
                  disabled={!selectedQuote || !amlCheckQuote}
                  operationType={OPERATION_TYPE.REQUEST_APPROVE}
                  quoteRequestData={quoteRequestData}
                  btnTitle="Принять условия"
                  btnTooltip={
                    !selectedQuote
                      ? "Нет выбранной котировки"
                      : !amlCheckQuote
                        ? "Нельзя принять котировку до поступления криптовалюты и окончания всех проверок"
                        : undefined
                  }
                  modalTitle="Принять условия"
                  messageTitle={`Принимаю условия на${" "}
                ${bidCard.bid.contract.direction === "EXP" ? "продажу " : "покупку "}цифровой валюты со следующими параметрами:`}
                  tooltip={`Данное действие является ${!DIRECT_PAYMENT_CUSTOMER_LP ? "Юридически значимым" : "подписанием Договора купли-продажи ЦВ и является юридически значимым"}`}
                />
              )}
            </Col>
          </Flex>
        </Flex>
      </WhiteBoxBlock>
    </div>
  );
});
